import React, { useCallback } from 'react';
import clsx from 'clsx';
import { SelectPicker } from 'rsuite';
import { FieldShell } from 'components/ui/FieldShell';
import { useFormLocked } from 'components/ui/FormLock';
import { Icon } from 'components/ui/Icon';
import controlStyles from 'components/ui/FieldShell/fieldControl.module.scss';
import styles from './SelectField.module.scss';

function SelectCaret() {
  return (
    <span className={controlStyles.trailing}>
      <Icon name="chevron-down" size={20} />
    </span>
  );
}

export interface ISelectOption {
  label: string;
  value: string;
}

export interface ISelectFieldProps {
  label: string;
  id: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: ISelectOption[];
  error?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  searchable?: boolean;
  onBlur?: () => void;
  className?: string;
}

export default function SelectField({
  label,
  id,
  value,
  onChange,
  options,
  error,
  disabled = false,
  required = false,
  placeholder = 'Select…',
  searchable = false,
  onBlur,
  className,
}: ISelectFieldProps) {
  const locked = useFormLocked();
  const isDisabled = disabled || locked;
  const errorId = error ? `${id}-error` : undefined;

  const handleChange = useCallback(
    (next: string | null) => {
      if (isDisabled) return;
      onChange(next);
    },
    [isDisabled, onChange],
  );

  return (
    <FieldShell
      label={label}
      id={id}
      error={error}
      disabled={isDisabled}
      required={required}
      className={className}
    >
      <div
        className={clsx(
          styles.wrap,
          error && styles.wrapError,
          isDisabled && styles.wrapDisabled,
        )}
      >
        <SelectPicker
          id={id}
          data={options}
          value={value}
          onChange={handleChange}
          onClose={onBlur}
          disabled={isDisabled}
          placeholder={placeholder}
          searchable={searchable && !isDisabled}
          cleanable={!isDisabled}
          open={isDisabled ? false : undefined}
          block
          className={styles.picker}
          popupClassName={styles.menu}
          caretAs={SelectCaret}
          renderValue={(current, item) => item?.label || current || placeholder}
          aria-invalid={error ? true : undefined}
          aria-describedby={errorId}
        />
      </div>
    </FieldShell>
  );
}
